

import { CalendarEvent } from '../hooks/useEvents';
import { AvailabilityObject } from '../types/schemas/abc';
import { Kind, OverlayEvent } from '../types/schemas/calendar';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/** Monday ⇒ 0 … Sunday ⇒ 6 (timezone-aware) */
export const dayIndex = (iso: string, tz?: string) => {
  if (tz) {
    const dayOfWeek = parseInt(formatInTimeZone(new Date(iso), tz, 'i'), 10); // 1=Mon, 7=Sun
    return dayOfWeek - 1; // 0=Mon, 6=Sun
  }
  return (new Date(iso).getDay() + 6) % 7;
};

/** 0 … 23 (timezone-aware) */
export const hourIndex = (iso: string, tz?: string) => {
  if (tz) return parseInt(formatInTimeZone(new Date(iso), tz, 'H'), 10);
  return new Date(iso).getHours();
};

/** Fractional hour for precise positioning (e.g. 14.5 = 2:30 PM) */
export const fractionalHour = (iso: string, tz?: string): number => {
  if (tz) {
    const h = parseInt(formatInTimeZone(new Date(iso), tz, 'H'), 10);
    const m = parseInt(formatInTimeZone(new Date(iso), tz, 'm'), 10);
    return h + m / 60;
  }
  const d = new Date(iso);
  return d.getHours() + d.getMinutes() / 60;
};

/** Mandatory row span ≥ 1  */
export const hourSpan = (startIso: string, endIso: string) => {
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
    return Math.max(1, Math.ceil(ms / 3600_000));
  };


export const formatTime = (date: string | Date, tz?: string) => {
  if (tz) return formatInTimeZone(new Date(typeof date === 'string' ? date : date.toISOString()), tz, 'HH:mm');
  return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const getMondayBasedIndex = (dateStr: string, tz?: string) => dayIndex(dateStr, tz);

export const adaptApiEvents = (events: CalendarEvent[] = [], tz?: string): OverlayEvent[] => {
  return events.map((e) => {
    const kind: Kind = e.eventType === 'lesson' ? 'LESSON' : 'INTRO';
    const title = e.title || (kind === 'LESSON' ? 'Lesson' : 'Intro call');
    return {
      id: e.id,
      kind,
      start: e.startAt,
      end: e.endAt,
      title,
      description: `${title} • ${formatTime(e.startAt, tz)} – ${formatTime(e.endAt, tz)}`,
    };
  });
};

export const generateFreeSlots = (availability: AvailabilityObject | null, weekStart: Date, homeTz?: string): OverlayEvent[] => {
  if (!availability) return [];
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

  return days.flatMap((dayName, index) => {
    const slots = availability[dayName] || [];
    const baseDate = new Date(weekStart);
    baseDate.setDate(weekStart.getDate() + index);

    return slots.map((slot) => {
      const h1 = Math.floor(slot.start / 60);
      const m1 = slot.start % 60;
      const h2 = Math.floor(slot.end / 60);
      const m2 = slot.end % 60;

      let startIso: string;
      let endIso: string;

      if (homeTz) {
        // Build wall-clock times in home tz and convert to UTC
        const wallStart = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), h1, m1, 0, 0);
        const wallEnd = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), h2, m2, 0, 0);
        startIso = fromZonedTime(wallStart, homeTz).toISOString();
        endIso = fromZonedTime(wallEnd, homeTz).toISOString();
      } else {
        const start = new Date(baseDate);
        start.setHours(h1, m1, 0, 0);
        const end = new Date(baseDate);
        end.setHours(h2, m2, 0, 0);
        startIso = start.toISOString();
        endIso = end.toISOString();
      }

      return {
        id: `${slot.id ?? `${dayName}-${slot.start}`}_${index}`,
        kind: 'FREE',
        start: startIso,
        end: endIso,
        title: 'Available',
        description: `Available (${String(h1).padStart(2,'0')}:${String(m1).padStart(2,'0')} – ${String(h2).padStart(2,'0')}:${String(m2).padStart(2,'0')})`,
      };
    });
  });
};

export const mergeFreeBlocks = (events: OverlayEvent[]): OverlayEvent[] => {
  const free = events.filter(e => e.kind === 'FREE')
    .sort((a, b) => +new Date(a.start) - +new Date(b.start));

  const merged: OverlayEvent[] = [];

  for (const ev of free) {
    if (merged.length === 0) {
      merged.push({...ev});
      continue;
    }

    const last = merged[merged.length - 1];
    if (last && +new Date(ev.start) <= +new Date(last.end)) {
      if (+new Date(ev.end) > +new Date(last.end)) last.end = ev.end;
    } else {
      merged.push({ ...ev });
    }
  }

  return [...events.filter(e => e.kind !== 'FREE'), ...merged];
};

export const splitOvernightEvents = (events: OverlayEvent[], tz?: string): OverlayEvent[] => {
  return events.flatMap(ev => {
    const startDay = tz
      ? formatInTimeZone(new Date(ev.start), tz, 'yyyy-MM-dd')
      : new Date(ev.start).toDateString();
    const endDay = tz
      ? formatInTimeZone(new Date(ev.end), tz, 'yyyy-MM-dd')
      : new Date(ev.end).toDateString();

    if (startDay === endDay) return [ev];

    // Check if end is midnight (no split needed)
    const endH = tz
      ? parseInt(formatInTimeZone(new Date(ev.end), tz, 'H'), 10)
      : new Date(ev.end).getHours();
    const endM = tz
      ? parseInt(formatInTimeZone(new Date(ev.end), tz, 'm'), 10)
      : new Date(ev.end).getMinutes();

    if (endH === 0 && endM === 0 && (new Date(ev.end).getTime() - new Date(ev.start).getTime() <= 86400000)) {
      return [ev];
    }

    // Split at midnight in the active timezone
    let endOfFirstDay: Date;
    let startOfNextDay: Date;

    if (tz) {
      // Get the start date string in the active tz, then build midnight of the next day
      const startDateStr = formatInTimeZone(new Date(ev.start), tz, 'yyyy-MM-dd');
      const [y, m, d] = startDateStr.split('-').map(Number);
      // End of first day = 23:59:59 in the active tz
      endOfFirstDay = fromZonedTime(new Date(y, m - 1, d, 23, 59, 59, 999), tz);
      // Start of next day = 00:00:00 in the active tz
      startOfNextDay = fromZonedTime(new Date(y, m - 1, d + 1, 0, 0, 0, 0), tz);
    } else {
      const start = new Date(ev.start);
      endOfFirstDay = new Date(start);
      endOfFirstDay.setHours(23, 59, 59, 999);
      startOfNextDay = new Date(start);
      startOfNextDay.setDate(startOfNextDay.getDate() + 1);
      startOfNextDay.setHours(0, 0, 0, 0);
    }

    return [
     {
       ...ev,
       end: endOfFirstDay.toISOString(),
       id: `${ev.id}_head`,
       origStart: ev.start,
       origEnd: ev.end,
       continuation: 'head'
     },
     {
       ...ev,
       start: startOfNextDay.toISOString(),
       id: `${ev.id}_tail`,
       origStart: ev.start,
       origEnd: ev.end,
       continuation: 'tail'
     }
   ];
  })
}
